/**
 * Agentic Helper Utilities
 * Manages AI agent decision making, message history, and structured response parsing
 */

import { callCopilotCustom } from './api';

// Global agent message history state
let globalAgentMessageHistory = [];

/**
 * Agent response parser - extracts structured selections from agent responses
 */
export class AgentResponseParser {
  static parseJSON(content) {
    try {
      // Look for JSON content in markdown code blocks
      const jsonMatch = content.match(/```json\s*(\{[\s\S]*?\})\s*```/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[1]);
      }
      
      // Try parsing the content directly
      if (content.trim().startsWith('{') && content.trim().endsWith('}')) {
        return JSON.parse(content);
      }
      
      // Look for JSON anywhere in the content
      const jsonRegex = /\{[\s\S]*?\}/g;
      const matches = content.match(jsonRegex);
      if (matches) {
        for (const match of matches) {
          try {
            return JSON.parse(match);
          } catch (e) {
            continue;
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('🚨 AGENT PARSER: Error parsing JSON response:', error);
      return null;
    }
  }

  static extractSelections(agentResponse) {
    const content = agentResponse.custom_chat_response?.content;
    if (!content) {
      console.error('🚨 AGENT PARSER: No content in agent response');
      return null;
    }

    console.log('🔍 AGENT PARSER: Parsing content:', content.substring(0, 200) + '...');

    const parsed = this.parseJSON(content);
    if (!parsed) {
      console.error('🚨 AGENT PARSER: Failed to parse JSON from content');
      return null;
    }

    console.log('✅ AGENT PARSER: Successfully parsed JSON:', parsed);

    // Handle different response structures
    const result = {
      selected_options: parsed.selected_options || parsed.selections || parsed.selected_titles || [],
      reasoning: parsed.reasoning || parsed.explanation || parsed.rationale || 'Agent made selections based on context',
      confidence: parsed.confidence || 0.8,
      raw_response: parsed
    };

    console.log('🎯 AGENT PARSER: Extracted selections:', result);
    return result;
  }
}

/**
 * Agent Message History Manager
 */
export class AgentMessageHistory {
  static getHistory() {
    return [...globalAgentMessageHistory];
  }

  static updateHistory(messageHistory) {
    if (Array.isArray(messageHistory)) {
      // Keep only the last 10 messages to avoid token limits
      globalAgentMessageHistory = messageHistory.slice(-10);
      console.log('📝 AGENT HISTORY: Updated with', globalAgentMessageHistory.length, 'messages');
    }
  }

  static addUserMessage(content) {
    globalAgentMessageHistory.push({
      is_auto_message: false,
      role: "user",
      content
    });
    this.trimHistory();
  }

  static addAgentMessage(content) {
    globalAgentMessageHistory.push({
      is_auto_message: false,
      role: "assistant",
      content
    });
    this.trimHistory();
  }

  static trimHistory() {
    // Keep only last 10 messages to manage token usage
    if (globalAgentMessageHistory.length > 10) {
      globalAgentMessageHistory = globalAgentMessageHistory.slice(-10);
    }
  }

  static clearHistory() {
    globalAgentMessageHistory = [];
    console.log('🧹 AGENT HISTORY: Cleared message history');
  }

  static getContextSummary() {
    const recentMessages = globalAgentMessageHistory.slice(-5);
    const contextPieces = recentMessages
      .filter(msg => msg.role === 'user' || msg.role === 'assistant')
      .map(msg => `${msg.role}: ${msg.content.substring(0, 100)}...`)
      .join('\n');
    
    return contextPieces || 'No previous context';
  }
}

/**
 * Agent Selection Service
 */
export class AgentSelectionService {
  // Track active selection requests to prevent duplicates
  static activeSelections = new Map();
  
  static async makeSelection(options, context = {}) {
    const {
      joid = '672a79fa33beba00fa948ba9', // Use the working joid from the curl example
      topic = 'selection',
      availableOptions = [],
      currentContext = '',
      systemMessage = 'You are a recruiter helping to calibrate candidate search criteria',
      selectionType = 'multi-select' // 'multi-select', 'single-select', 'range'
    } = context;

    // Create deduplication key based on context
    const deduplicationKey = `${topic}_${joid}_${availableOptions.length}_${currentContext.slice(0, 50)}`;
    
    // Check if this exact selection is already in progress
    if (this.activeSelections.has(deduplicationKey)) {
      console.log('🚫 AGENT SELECTION: Duplicate selection request prevented for:', deduplicationKey);
      const existingPromise = this.activeSelections.get(deduplicationKey);
      return await existingPromise; // Return the existing promise result
    }

    try {
      // Format options for agent
      const optionsText = this.formatOptionsForAgent(availableOptions, selectionType);
      
      // Create structured prompt
      const baseMessage = this.createSelectionPrompt(topic, optionsText, currentContext, selectionType);
      
      // Get current message history
      const messageHistory = AgentMessageHistory.getHistory();

      // Add context about current selection
      AgentMessageHistory.addUserMessage(`Context: ${currentContext}\nNeed to select ${selectionType} from ${topic} options.`);

      console.log('🤖 AGENT SELECTION: Making selection for topic:', topic, 'with', availableOptions.length, 'options');
      
      // Create selection promise and store it to prevent duplicates
      const selectionPromise = this.executeSelection(joid, systemMessage, baseMessage, messageHistory, topic, availableOptions);
      this.activeSelections.set(deduplicationKey, selectionPromise);

      const result = await selectionPromise;
      
      // Clean up after completion
      this.activeSelections.delete(deduplicationKey);
      
      return result;
      
    } catch (error) {
      // Clean up on error
      this.activeSelections.delete(deduplicationKey);
      console.error('🚨 AGENT SELECTION: Error making selection:', error);
      throw error;
    }
  }

  static async executeSelection(joid, systemMessage, baseMessage, messageHistory, topic, availableOptions) {
    try {
      console.log('🤖 AGENT SELECTION: About to call copilot-custom API with:', {
        joid,
        systemMessage: systemMessage.substring(0, 100) + '...',
        baseMessage: baseMessage.substring(0, 200) + '...',
        messageHistoryLength: messageHistory.length,
        topic,
        availableOptionsCount: availableOptions.length
      });

      // Call agent
      const agentResponse = await callCopilotCustom(
        joid,
        systemMessage,
        baseMessage,
        messageHistory,
        { 
          enforce_json: true, 
          custom_purpose: `${topic}_selection` 
        }
      );

      console.log('🤖 AGENT SELECTION: Received response from API:', {
        cxt_id: agentResponse?.cxt_id,
        has_content: !!agentResponse?.custom_chat_response?.content,
        content_preview: agentResponse?.custom_chat_response?.content?.substring(0, 200) + '...'
      });

      // Update message history with agent's response
      AgentMessageHistory.updateHistory(agentResponse.custom_chat_response?.message_history || []);

      // Parse agent's selection
      const selection = AgentResponseParser.extractSelections(agentResponse);
      
      if (selection) {
        console.log('✅ AGENT SELECTION: Successfully parsed selections:', {
          topic,
          selected_count: selection.selected_options?.length || 0,
          reasoning: selection.reasoning.substring(0, 100) + '...'
        });
        return selection;
      } else {
        throw new Error('Failed to parse agent selection response');
      }

    } catch (error) {
      console.error('🚨 AGENT SELECTION: Error in executeSelection:', error);
      throw error;
    }
  }

  static formatOptionsForAgent(options, selectionType) {
    if (selectionType === 'range') {
      return `Range: ${options.min || 0} to ${options.max || 100}`;
    }

    if (Array.isArray(options)) {
      return options.map((option, index) => {
        if (typeof option === 'string') {
          return `${index + 1}. ${option}`;
        } else if (option.label && option.value) {
          return `${index + 1}. ${option.label} (value: ${option.value})`;
        } else {
          return `${index + 1}. ${JSON.stringify(option)}`;
        }
      }).join('\n');
    }

    return JSON.stringify(options);
  }

  static createSelectionPrompt(topic, optionsText, currentContext, selectionType) {
    const selectionInstruction = selectionType === 'single-select' 
      ? 'Select exactly ONE option' 
      : selectionType === 'range'
      ? 'Select a range with min and max values'
      : 'Select 2-4 most relevant options';

    return `You are a recruiter helping to select the best ${topic} for a job posting. Based on the context and requirements below, select the most appropriate options.

CONTEXT & REQUIREMENTS:
${currentContext}

AVAILABLE OPTIONS:
${optionsText}

TASK: ${selectionInstruction} from the list above that best match the job requirements and context.

IMPORTANT: Respond with ONLY a JSON object in this exact format, no other text:

{
  "selected_options": ["exact_option_1", "exact_option_2"],
  "reasoning": "Brief explanation of selection rationale",
  "confidence": 0.85
}

Rules:
- Use the EXACT text from the options list above
- ${selectionType === 'single-select' ? 'Select only ONE option' : 'Select 2-4 most relevant options'}
- Be strategic - don't select too many or too few options
- Base selections on typical hiring needs for this context`;
  }
}

/**
 * Agentic Flow Manager - Main orchestrator
 */
export class AgenticFlowManager {
  static isAgenticModeEnabled = false;
  static currentSessionId = null;

  static enableAgenticMode(sessionId) {
    this.isAgenticModeEnabled = true;
    this.currentSessionId = sessionId;
    console.log('🤖 AGENTIC FLOW: Enabled for session:', sessionId);
  }

  static disableAgenticMode() {
    this.isAgenticModeEnabled = false;
    this.currentSessionId = null;
    AgentMessageHistory.clearHistory();
    console.log('👤 MANUAL MODE: Disabled agentic flow');
  }

  static isEnabled() {
    return this.isAgenticModeEnabled;
  }

  static async handleStrategySelection(strategyContext) {
    if (!this.isEnabled()) {
      return null; // Fall back to manual selection
    }

    try {
      console.log('🤖 AGENTIC FLOW: Handling strategy selection for:', strategyContext.topic);
      
      const selection = await AgentSelectionService.makeSelection(
        strategyContext.options,
        {
          joid: '672a79fa33beba00fa948ba9', // Use valid joid for testing
          topic: strategyContext.topic,
          availableOptions: strategyContext.options,
          currentContext: strategyContext.context || '',
          selectionType: strategyContext.selectionType || 'multi-select',
          systemMessage: strategyContext.systemMessage || 'You are a recruiter helping to calibrate candidate search criteria'
        }
      );

      return selection;
    } catch (error) {
      console.error('🚨 AGENTIC FLOW: Error in strategy selection, falling back to manual:', error);
      return null; // Fall back to manual selection
    }
  }

  static getAgentStatus() {
    return {
      enabled: this.isEnabled(),
      session_id: this.currentSessionId,
      message_history_length: AgentMessageHistory.getHistory().length,
      context_summary: AgentMessageHistory.getContextSummary()
    };
  }
}

// Export convenience functions
export const enableAgenticMode = AgenticFlowManager.enableAgenticMode.bind(AgenticFlowManager);
export const disableAgenticMode = AgenticFlowManager.disableAgenticMode.bind(AgenticFlowManager);
export const isAgenticModeEnabled = AgenticFlowManager.isEnabled.bind(AgenticFlowManager);
export const handleAgentSelection = AgenticFlowManager.handleStrategySelection.bind(AgenticFlowManager);
export const getAgentStatus = AgenticFlowManager.getAgentStatus.bind(AgenticFlowManager);